var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
import { injectable, inject } from '@servicetitan/react-ioc';
import { CULTURE_TOKEN } from '@servicetitan/culture';
function ordinalString(num) {
    switch (num % 100) {
        case 11:
        case 12:
        case 13:
            return num.toString() + 'th';
        default:
            switch (num % 10) {
                case 1:
                    return num.toString() + 'st';
                case 2:
                    return num.toString() + 'nd';
                case 3:
                    return num.toString() + 'rd';
                default:
                    return num.toString() + 'th';
            }
    }
}
let DateTimeUtils = class DateTimeUtils {
    constructor(culture) {
        Object.defineProperty(this, "locale", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "dateFormatter", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "dateFullFormatter", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "shortTimeFormatter", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "fullTimeFormatter", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "shortWeekdayFormatter", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "shortMonthFormatter", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "shortMonthDateFormatter", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "fullMonthFormatter", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "fullMonthDateFormatter", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "short24HourTimeFormatter", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "medium24HourTimeFormatter", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /**
         * General date formatting.
         *
         * @example
         * format(date, {
         *     weekday: 'long',
         *     month: 'long',
         *     day: 'numeric'
         * }); // Tuesday, December 3
         */
        Object.defineProperty(this, "format", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (date, options) => {
                return date.toLocaleDateString(this.locale, options);
            }
        });
        /**
         * Equals moment format: 'M/D/YY'
         * Returns: 1/2/20
         */
        Object.defineProperty(this, "formatDate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (date) => {
                return this.dateFormatter.format(date);
            }
        });
        /**
         * Equals moment format: 'MMM DY'
         * Returns: Dec 2
         */
        Object.defineProperty(this, "formatDateShort", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (date) => {
                return this.shortMonthDateFormatter.format(date);
            }
        });
        /**
         * Equals moment format: 'MM/DD/YYYY', 'MM/DD/Y', with US locale - 'L'
         * Returns: 01/02/2020
         */
        Object.defineProperty(this, "formatDateFull", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (date) => {
                return this.dateFullFormatter.format(date);
            }
        });
        /**
         * Equals moment format: 'MM/DD/YYYY hh:mm A', with US locale - 'L LT'
         * Returns: 01/02/2020 03:47 AM
         */
        Object.defineProperty(this, "formatDateTimeFull", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (date) => {
                return `${this.formatDateFull(date)} ${this.formatFullTime(date)}`;
            }
        });
        /**
         * Equals moment format: 'M/D/YY hh:mm A', with US locale - 'L LT'
         * Returns: 1/2/20 3:47 AM
         */
        Object.defineProperty(this, "formatDateTimeShort", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (date) => {
                return `${this.formatDate(date)} ${this.formatShortTime(date)}`;
            }
        });
        /**
         * Equals moment format: 'MMM D, hh:mm A', with US locale - 'L LT'
         * Returns: Dec 2 3:47 AM
         */
        Object.defineProperty(this, "formatDateTimeShortest", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (date) => {
                return `${this.formatDateShort(date)} ${this.formatShortTime(date)}`;
            }
        });
        /**
         * Equals moment format: 'h:mm A', with US locale - 'LT'
         * Returns: 3:00 PM
         */
        Object.defineProperty(this, "formatShortTime", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (date) => {
                return this.shortTimeFormatter.format(date);
            }
        });
        /**
         * Equals moment format: 'hh:mm A'
         * Returns: 03:00 PM
         */
        Object.defineProperty(this, "formatFullTime", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (date) => {
                return this.fullTimeFormatter.format(date);
            }
        });
        /**
         * Equals moment format: 'hh:mm'
         * Returns: 15:00
         */
        Object.defineProperty(this, "formatShort24HourTime", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (date) => {
                return this.short24HourTimeFormatter.format(date);
            }
        });
        /**
         * Equals moment format: 'hh:mm:ss'
         * Returns: 15:00:00
         */
        Object.defineProperty(this, "formatMedium24HourTime", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (date) => {
                return this.medium24HourTimeFormatter.format(date);
            }
        });
        /**
         * Equals moment format: 'ddd'
         * Returns: Thu
         */
        Object.defineProperty(this, "formatShortWeekday", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (date) => {
                return this.shortWeekdayFormatter.format(date);
            }
        });
        /**
         * Equals moment format: 'MMM'
         * Returns: Jan
         */
        Object.defineProperty(this, "formatShortMonth", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (date) => {
                return this.shortMonthFormatter.format(date);
            }
        });
        /**
         * Equals moment format: 'MMMM'
         * Returns: January
         */
        Object.defineProperty(this, "formatFullMonth", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (date) => {
                return this.fullMonthFormatter.format(date);
            }
        });
        /**
         * Equals moment format: 'MMMM D, YYYY', with US locale - 'LL'
         * Returns: January 2, 2020
         */
        Object.defineProperty(this, "formatFullMonthDate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (date) => {
                return this.fullMonthDateFormatter.format(date);
            }
        });
        /**
         * Equals moment format: 'MMM Do, YYYY'
         * Returns: Jan 2nd, 2020
         */
        Object.defineProperty(this, "formatShortMonthOrdinalDate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (date) => {
                const ordinalDate = ordinalString(date.getDate());
                return `${this.formatShortMonth(date)} ${ordinalDate}, ${date.getFullYear()}`;
            }
        });
        /**
         * Equals moment format: 'MMM D YYYY'
         * Returns: Jan 2 2020
         */
        Object.defineProperty(this, "formatShortMonthDate", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (date) => {
                return `${this.formatShortMonth(date)} ${date.getDate()} ${date.getFullYear()}`;
            }
        });
        Object.defineProperty(this, "getLocale", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: () => {
                return this.locale;
            }
        });
        this.locale = culture.Name;
        this.dateFormatter = new Intl.DateTimeFormat(this.locale, {
            day: 'numeric',
            month: 'numeric',
            year: '2-digit',
        });
        this.dateFullFormatter = new Intl.DateTimeFormat(this.locale, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
        this.shortTimeFormatter = new Intl.DateTimeFormat(this.locale, {
            hour: 'numeric',
            minute: '2-digit',
        });
        this.fullTimeFormatter = new Intl.DateTimeFormat(this.locale, {
            hour: '2-digit',
            minute: '2-digit',
        });
        this.short24HourTimeFormatter = new Intl.DateTimeFormat(this.locale, {
            hour: '2-digit',
            minute: '2-digit',
            hourCycle: 'h23',
        });
        this.medium24HourTimeFormatter = new Intl.DateTimeFormat(this.locale, {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hourCycle: 'h23',
        });
        this.shortWeekdayFormatter = new Intl.DateTimeFormat(this.locale, { weekday: 'short' });
        this.shortMonthFormatter = new Intl.DateTimeFormat(this.locale, { month: 'short' });
        this.shortMonthDateFormatter = new Intl.DateTimeFormat(this.locale, {
            month: 'short',
            day: 'numeric',
        });
        this.fullMonthFormatter = new Intl.DateTimeFormat(this.locale, { month: 'long' });
        this.fullMonthDateFormatter = new Intl.DateTimeFormat(this.locale, {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
        });
    }
};
DateTimeUtils = __decorate([
    injectable(),
    __param(0, inject(CULTURE_TOKEN)),
    __metadata("design:paramtypes", [Object])
], DateTimeUtils);
export { DateTimeUtils };
//# sourceMappingURL=index.js.map